import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { PdlRemarkCard } from '../../lib/pdlRemarkPresentation';
import { PDL_CHIP_SX, PDL_INNER_RADIUS, PDL_SECTION_LABEL_SX } from '../../lib/pdlDecisionChrome';

type Group = {
  category: string;
  categoryLabel: string;
  cards: PdlRemarkCard[];
};

function impactLabel(card: PdlRemarkCard): { text: string; affectsOffer: boolean } {
  if (card.code.startsWith('BINDING_')) {
    return { text: 'Set the offer limit', affectsOffer: true };
  }
  if (card.code === 'CAPPED_TO_REQUESTED') {
    return { text: 'Capped to request', affectsOffer: true };
  }
  if (card.code === 'JURISDICTION_BLOCKED' || card.code === 'FEDERAL_AMOUNT_EXCEEDED') {
    return { text: 'Hard stop', affectsOffer: true };
  }
  return { text: 'Note only', affectsOffer: false };
}

function cardSummary(card: PdlRemarkCard): string {
  const parts = card.factors
    .filter((f) => f.label !== 'Decision impact')
    .map((f) => `${f.label}: ${f.value}`);
  return parts.join(' · ');
}

function SignalRow({ card }: { card: PdlRemarkCard }) {
  const theme = useTheme();
  const impact = impactLabel(card);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        alignItems: 'flex-start',
        py: 0.85,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ pt: 0.15, flexShrink: 0, color: impact.affectsOffer ? 'warning.main' : 'text.disabled' }}>
        {impact.affectsOffer ? (
          <WarningAmberRoundedIcon sx={{ fontSize: 18 }} aria-hidden />
        ) : (
          <InfoOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
            {card.title}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={impact.text}
            sx={{
              ...PDL_CHIP_SX,
              fontSize: '0.68rem',
              borderColor: impact.affectsOffer
                ? alpha(theme.palette.warning.main, 0.5)
                : 'divider',
            }}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45, display: 'block' }}>
          {cardSummary(card)}
        </Typography>
      </Box>
    </Box>
  );
}

export function PdlPolicySignalsPanel({ groups }: { groups: Group[] }) {
  if (groups.length === 0) return null;

  return (
    <Stack spacing={1.25} data-testid="application-decision-engine-pdl-policy-signals">
      {groups.map((group) => (
        <Box key={group.category}>
          <Typography variant="caption" sx={{ ...PDL_SECTION_LABEL_SX, display: 'block', mb: 0.5 }}>
            {group.categoryLabel}
          </Typography>
          <Box
            sx={{
              borderRadius: PDL_INNER_RADIUS,
              border: '1px solid',
              borderColor: 'divider',
              px: 1.1,
              bgcolor: 'background.paper',
            }}
          >
            {group.cards.map((card) => (
              <SignalRow key={card.code} card={card} />
            ))}
          </Box>
        </Box>
      ))}
    </Stack>
  );
}
